import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { CreditCard, Calendar, CheckCircle, XCircle, Clock, Loader2, Package, Crown, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubscriptionManager = ({ isOpen, onClose }) => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const [activeTab, setActiveTab] = useState('subscription');

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, histRes] = await Promise.all([
        axios.get(`${API}/subscription`),
        axios.get(`${API}/payments/history`)
      ]);
      setSubscription(subRes.data);
      setTransactions(histRes.data.transactions);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('¿Estás seguro de que deseas cancelar tu suscripción? Perderás acceso a las funciones premium.')) {
      return;
    }
    
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`);
      toast.success('Suscripción cancelada');
      await refreshUser();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="flex items-center gap-1 text-green-400"><CheckCircle className="w-3 h-3" /> Pagado</span>;
      case 'pending':
        return <span className="flex items-center gap-1 text-yellow-400"><Clock className="w-3 h-3" /> Pendiente</span>;
      default:
        return <span className="flex items-center gap-1 text-red-400"><XCircle className="w-3 h-3" /> {status}</span>;
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-2xl glass rounded-2xl overflow-hidden max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="subscription-modal"
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">Gestión de Suscripción</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('subscription')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${activeTab === 'subscription' ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]' : 'text-gray-400 hover:text-white'}`}
          >
            <Crown className="w-4 h-4 inline-block mr-2" />
            Suscripción
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${activeTab === 'history' ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]' : 'text-gray-400 hover:text-white'}`}
          >
            <CreditCard className="w-4 h-4 inline-block mr-2" />
            Historial de Pagos
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00]" />
            </div>
          ) : activeTab === 'subscription' ? (
            <div className="space-y-6">
              <div className="glass rounded-xl p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {subscription?.current_plan === 'free' ? (
                      <Package className="w-8 h-8 text-gray-400" />
                    ) : (
                      <Crown className="w-8 h-8 text-[#FF6B00]" />
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        Plan {subscription?.plan_info?.name || 'Gratuito'}
                      </h3>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-400">
                          {subscription?.current_plan === 'free' ? 'Sin suscripción activa' : 
                           subscription?.subscription_status === 'active' ? 'Suscripción activa' : 
                           subscription?.subscription_status || 'Activa'}
                        </p>
                        {subscription?.subscription_auto_renew && (
                          <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Auto-renovación</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {subscription?.plan_info?.price && (
                    <div className="text-right">
                      <p className="text-2xl font-bold text-white">${subscription.plan_info.price}</p>
                      <p className="text-sm text-gray-400">/{subscription.plan_info.billing === 'annual' ? 'año' : 'mes'}</p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Créditos disponibles</p>
                    <p className="text-xl font-bold text-white">{subscription?.credits || 0}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Créditos por período</p>
                    <p className="text-xl font-bold text-white">{subscription?.plan_info?.credits || 0}</p>
                  </div>
                </div>

                {subscription?.subscription_ends && subscription?.current_plan !== 'free' && (
                  <div className="bg-white/5 rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-500 mb-1">
                      {subscription?.subscription_auto_renew ? 'Próxima renovación' : 'Expira'}
                    </p>
                    <p className="text-sm font-medium text-white">
                      {new Date(subscription.subscription_ends).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                )}

                {subscription?.current_plan !== 'free' && (
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    className="w-full py-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2"
                    data-testid="cancel-subscription-btn"
                  >
                    {cancelling ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4" />
                        Cancelar suscripción
                      </>
                    )}
                  </button>
                )}
              </div>

              {subscription?.last_payment && (
                <div className="glass rounded-xl p-4 border border-white/10">
                  <p className="text-xs text-gray-500 mb-2">Último pago</p>
                  <div className="flex items-center justify-between">
                    <span className="text-white">${subscription.last_payment.amount}</span>
                    <span className="text-sm text-gray-400">
                      {new Date(subscription.last_payment.created_at).toLocaleDateString('es')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-500">No hay transacciones</p>
                </div>
              ) : (
                transactions.map((t) => (
                  <div key={t.id} className="glass rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.payment_type === 'subscription' ? 'bg-purple-500/20' : 'bg-emerald-500/20'}`}>
                          {t.payment_type === 'subscription' ? (
                            <Crown className="w-5 h-5 text-purple-400" />
                          ) : (
                            <Package className="w-5 h-5 text-emerald-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{t.item_name}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(t.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-semibold">${t.amount}</p>
                        <div className="text-xs">{getStatusBadge(t.payment_status)}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};
